import React, { useEffect, useState } from 'react';
import { OutliersDataItem } from 'widgets/outliers/data/outliersData';
import {
    OutliersColumn,
    useOutlierColumnDefinitions,
} from 'widgets/outliers/components/grid/columns';
import { useWidgetGridState } from 'widgets/store/grid/useWidgetGridState';
import { Grid, GridApi } from '@instinet-analytics/semantic-ui-components';
import { getRowClass } from 'widgets/blotter/blotterGridOptions';
import 'widgets/blotter/OrderGrid.scss';
import { GetRowClassParams } from '@instinet-analytics/semantic-ui-components';
import './OutliersGrid.scss';
import { OutliersGridPanelProps } from 'widgets/outliers/components/grid/OutliersGridPanel';
import { useOutlierWidgetDimensions } from 'widgets/outliers/components/hooks';
import { useWidgetEditedColumnNames } from 'grid/columnNames/hooks';

export interface OutliersGridProps extends OutliersGridPanelProps {
    onGridApiSet: (gridApi: GridApi) => void;
    height: number;
}

interface RowData extends OutliersDataItem {
    highlighted: boolean;
}

export function OutliersGrid(props: OutliersGridProps) {
    const {
        data,
        height,
        onGridApiSet,
        highlightedOrderIds,
        selectedOrderIds,
        onSetSelectedOrderIds,
    } = props;

    const { colDefs, columnDefinitions } = useOutlierColumnDefinitions();
    const { dockablePanelWidth: width } = useOutlierWidgetDimensions();

    const [rowData, setRowData] = useState<RowData[]>([]);
    const [gridApi, setGridApi] = useState<GridApi | undefined>();
    const useWidgetEditedColumnNamesProps = useWidgetEditedColumnNames();

    useEffect(() => {
        const nextRowData = (data?.items ?? []).map((d) => ({
            ...d,
            highlighted: highlightedOrderIds.indexOf(d.orderID.toString()) > -1,
        }));
        setRowData(nextRowData);
    }, [data, highlightedOrderIds]);

    useEffect(() => {
        if (gridApi && selectedOrderIds.length > 0) {
            const startId = selectedOrderIds[0];
            const endId =
                selectedOrderIds.length > 1
                    ? selectedOrderIds[selectedOrderIds.length - 1]
                    : undefined;
            gridApi.ensureRowVisible(startId, endId);
        }
    }, [selectedOrderIds, gridApi]);

    const { gridPropsFromState, columnDefs } = useWidgetGridState<
        RowData,
        OutliersColumn
    >(columnDefinitions, colDefs, { disableSorting: true });

    const onGridReady = (api: GridApi) => {
        setGridApi(api);
        onGridApiSet(api);
    };

    const style = { height: `${height}px`, width: `${width}px` };

    return (
        <div className="OutliersGridContainer" style={style}>
            <Grid<RowData>
                height={height}
                className="OutliersGrid OrderGrid"
                columnDefs={columnDefs}
                rowData={rowData}
                suppressColumnVirtualisation={false}
                onGridReady={onGridReady}
                selectedRowIds={selectedOrderIds}
                deselectCurrentSelectionOnSetSelectedRowIds={true}
                onSelectedRowIdsChanged={onSetSelectedOrderIds}
                singleSelectOnly={false}
                getRowDataId={(r) => r.orderID}
                getRowClass={getRowClass}
                rowClassRules={getRowClassRules()}
                {...gridPropsFromState}
                {...useWidgetEditedColumnNamesProps}
            />
        </div>
    );
}

function getRowClassRules() {
    return {
        highlighted: (params: GetRowClassParams<RowData>) => {
            const item: RowData = params.data;
            return item.highlighted;
        },
    };
}
